import type { DiagramPitch } from '@ukinfo/shared';
import { PADDING, PITCH_HEIGHT, pitchViewBox } from './PitchDiagram';

/**
 * Reine Rechnung fuer Zoom und Schwenken einer Skizze - ohne React.
 *
 * Der Zoom ist reine Ansicht: Er verkleinert die `viewBox` des SVG auf einen
 * Ausschnitt (`DiagramView`) und wird nie in der Skizze gespeichert. Der Ausschnitt
 * liegt immer ganz innerhalb der Zeichnung (Feld samt schmalem Rand), man kann also
 * nicht ins Leere schwenken. Alles rechnet in Zeichnungseinheiten wie die Skizze
 * (Feldbreite = 100, Ursprung links oben im Feld, der Rand liegt bei -PADDING).
 */

/** Stufen der Zoomknoepfe; die Zwei-Finger-Geste und das Mausrad zoomen stufenlos dazwischen. */
export const ZOOM_STEPS = [1, 1.5, 2, 3, 4] as const;
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

/** Toleranz, ab der ein stufenloser Zoom als "auf einer Stufe" gilt. */
const STEP_EPSILON = 0.01;

/** Ausschnitt der Zeichnung: Zoomfaktor und linke obere Ecke in Zeichnungseinheiten. */
export interface DiagramView {
  zoom: number;
  x: number;
  y: number;
}

export interface DrawingPoint {
  x: number;
  y: number;
}

export interface DrawingRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Auf dem Bildschirm belegte Flaeche des SVG (wie `getBoundingClientRect`). */
export interface ScreenRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function clampNumber(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Die ganze Zeichnung: Feld samt Rand. */
export function drawingBox(pitch: DiagramPitch): DrawingRect {
  return { x: -PADDING, y: -PADDING, w: 100 + 2 * PADDING, h: PITCH_HEIGHT[pitch] + 2 * PADDING };
}

/** Ganzes Feld: kein Zoom. */
export function fullView(pitch: DiagramPitch): DiagramView {
  const box = drawingBox(pitch);
  return { zoom: MIN_ZOOM, x: box.x, y: box.y };
}

export function isZoomed(view: DiagramView): boolean {
  return view.zoom > MIN_ZOOM + 1e-6;
}

/** Der sichtbare Ausschnitt in Zeichnungseinheiten. */
export function viewWindow(pitch: DiagramPitch, view: DiagramView): DrawingRect {
  const box = drawingBox(pitch);
  return { x: view.x, y: view.y, w: box.w / view.zoom, h: box.h / view.zoom };
}

/** Haelt Zoom in den Grenzen und den Ausschnitt innerhalb der Zeichnung. */
export function clampView(pitch: DiagramPitch, view: DiagramView): DiagramView {
  const zoom = clampNumber(Number.isFinite(view.zoom) ? view.zoom : MIN_ZOOM, MIN_ZOOM, MAX_ZOOM);
  const box = drawingBox(pitch);
  const w = box.w / zoom;
  const h = box.h / zoom;
  const x = Number.isFinite(view.x) ? view.x : box.x;
  const y = Number.isFinite(view.y) ? view.y : box.y;
  return {
    zoom,
    x: clampNumber(x, box.x, box.x + box.w - w),
    y: clampNumber(y, box.y, box.y + box.h - h),
  };
}

/** `viewBox` des SVG; ohne Zoom genau die der unzoomten Skizze. */
export function viewBoxOf(pitch: DiagramPitch, view: DiagramView): string {
  if (!isZoomed(view)) return pitchViewBox(pitch);
  const { x, y, w, h } = viewWindow(pitch, view);
  return [x, y, w, h].map((value) => Number(value.toFixed(3))).join(' ');
}

/**
 * Lage des Ausschnitts im Kasten des SVG. Das SVG zeichnet mit `meet`, zentriert:
 * Ist der Kasten breiter oder hoeher als der Ausschnitt, bleibt seitlich oder oben
 * und unten Platz.
 */
function placement(rect: ScreenRect, w: number, h: number): { scale: number; offsetX: number; offsetY: number } {
  const scale = Math.min(rect.width / w, rect.height / h);
  return { scale, offsetX: (rect.width - w * scale) / 2, offsetY: (rect.height - h * scale) / 2 };
}

/** Bildschirmpixel je Zeichnungseinheit bei der aktuellen Ansicht (0, wenn der Kasten nicht messbar ist). */
export function pixelsPerUnit(rect: ScreenRect, pitch: DiagramPitch, view: DiagramView = fullView(pitch)): number {
  const section = viewWindow(pitch, view);
  const { scale } = placement(rect, section.w, section.h);
  return Number.isFinite(scale) && scale > 0 ? scale : 0;
}

/** Bildschirmposition -> Zeichnungseinheiten (nicht gerundet, auch ausserhalb des Feldes). Ohne messbaren Kasten: null. */
export function screenToDrawing(clientX: number, clientY: number, rect: ScreenRect, pitch: DiagramPitch, view: DiagramView = fullView(pitch)): DrawingPoint | null {
  const section = viewWindow(pitch, view);
  const { scale, offsetX, offsetY } = placement(rect, section.w, section.h);
  if (!Number.isFinite(scale) || scale <= 0) return null;
  return {
    x: section.x + (clientX - rect.left - offsetX) / scale,
    y: section.y + (clientY - rect.top - offsetY) / scale,
  };
}

/**
 * Ausschnitt mit `zoom`, in dem der Zeichnungspunkt `point` an der Bildschirmposition
 * `client` erscheint. Damit laufen alle Gesten ueber dieselbe Rechnung: Zoom um einen
 * Punkt (Mausrad, Zwei-Finger-Geste, Knoepfe um die Mitte) und Schwenken (Zoom bleibt,
 * der angefasste Punkt folgt dem Finger). Das Ergebnis ist auf die Zeichnung begrenzt.
 */
export function viewAround(pitch: DiagramPitch, rect: ScreenRect, zoom: number, point: DrawingPoint, client: DrawingPoint): DiagramView {
  const next = clampNumber(zoom, MIN_ZOOM, MAX_ZOOM);
  const box = drawingBox(pitch);
  const w = box.w / next;
  const h = box.h / next;
  const { scale, offsetX, offsetY } = placement(rect, w, h);
  if (!Number.isFinite(scale) || scale <= 0) {
    // Kein messbarer Kasten (etwa noch nicht angezeigt): um den Punkt zentrieren.
    return clampView(pitch, { zoom: next, x: point.x - w / 2, y: point.y - h / 2 });
  }
  return clampView(pitch, {
    zoom: next,
    x: point.x - (client.x - rect.left - offsetX) / scale,
    y: point.y - (client.y - rect.top - offsetY) / scale,
  });
}

/** Zoom auf `zoom` um eine Bildschirmposition (Standard: Mitte der Zeichenflaeche). */
export function zoomAround(pitch: DiagramPitch, rect: ScreenRect, view: DiagramView, zoom: number, client?: DrawingPoint): DiagramView {
  const at = client ?? { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  const section = viewWindow(pitch, view);
  const point = screenToDrawing(at.x, at.y, rect, pitch, view) ?? { x: section.x + section.w / 2, y: section.y + section.h / 2 };
  return viewAround(pitch, rect, zoom, point, at);
}

/** Naechste Stufe in Richtung `direction` (1 = hinein, -1 = heraus); an den Enden bleibt der Wert. */
export function nextZoomStep(zoom: number, direction: 1 | -1): number {
  if (direction === 1) return ZOOM_STEPS.find((step) => step > zoom + STEP_EPSILON) ?? zoom;
  return [...ZOOM_STEPS].reverse().find((step) => step < zoom - STEP_EPSILON) ?? zoom;
}

/** Zoomstufe fuer die Anzeige: "1×", "1,5×", "2,3×". */
export function formatZoom(zoom: number): string {
  return `${String(Math.round(zoom * 10) / 10).replace('.', ',')}×`;
}
